import request from './base/request';
import SecureLS from 'secure-ls';

const ls = new SecureLS({ encodingType: 'aes' });
// const BASE_URL = 'http://localhost:7002/api';
// const BASE_URL = 'https://rentacourt-backend-pg.onrender.com/api';

const getBaseUrl = () => {
  // Check if we're running in browser
  if (typeof window !== 'undefined') {
    const isDevelopment = window.location.hostname === 'localhost';
    return isDevelopment 
      ? 'http://localhost:7002'
      : 'https://rentacourt-backend-pg.onrender.com';
  }
  // Fallback for server-side
  return process.env.NODE_ENV === 'production' 
    ? 'https://rentacourt-backend-pg.onrender.com'
    : 'http://localhost:7002';
};

const BASE_URL = `${getBaseUrl()}/api`;
// Helper to safely log API response data for debugging
const logResponseData = (functionName, response) => {
  if (response?.data) {
    if (Array.isArray(response.data)) {
    } else if (typeof response.data === 'object') {
    }
  }
};

// Get token from SecureLS with proper decryption
const getToken = () => {
  try {
    // Try to get token using SecureLS (this will decrypt it)
    const token = ls.get('token');
    if (token) {
      return token;
    }
  } catch (e) {
    console.error('Error accessing SecureLS:', e);
  }
  
  console.error('No authentication token found');
  return null;
};

export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/uploads/')) {
    const encodedPath = encodeURI(imagePath);
    return `${getBaseUrl()}${encodedPath}`;
  }
  return imagePath;
};

const admin = {
  admin_login: async (data) => {
    const response = await request.post('/users/signin', data);
    logResponseData('admin_login', response);
    return response;
  },
  
  get_transactions: async (page = 1, limit = 10) => {
    try {
      const response = await request.get(`/bookings/admin/all?page=${page}&limit=${limit}`, true);
      logResponseData('get_transactions', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error in get_transactions:', error);
      throw error;
    }
  },
  
  get_courts: async (page = 1, limit = 10) => {
    try {
      const response = await request.get(`/courts?page=${page}&limit=${limit}`, true);
      return response;
    } catch (error) {
      console.error('[Admin API] Error in get_courts:', error);
      return {
        status: 500,
        data: { success: false, message: error.message || 'Failed to fetch courts' }
      };
    }
  },
  
  create_court: async (data) => {
    const formattedData = {
      ...data
    };
    
    if (data.pricePerHour && !data.hourly_rate) {
      formattedData.hourly_rate = data.pricePerHour;
    }
    
    if (formattedData.facilities && !Array.isArray(formattedData.facilities)) {
      if (typeof formattedData.facilities === 'string') {
        formattedData.facilities = [formattedData.facilities];
      } else {
        try {
          const parsed = JSON.parse(formattedData.facilities);
          formattedData.facilities = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          formattedData.facilities = [String(formattedData.facilities)];
        }
      }
    }
    
    if (formattedData.availableDays && Array.isArray(formattedData.availableDays)) {
      formattedData.availableDays = formattedData.availableDays.map(day => 
        typeof day === 'string' ? day.toLowerCase() : day
      );
    }
    const response = await request.post('/courts', formattedData, true);
    logResponseData('create_court', response);
    return response;
  },
  
  update_court: async (id, data) => {
    try {
      return await request.put(`/courts/${id}`, data, true);
    } catch (error) {
      console.error('[Admin API] Error in update_court:', error);
      return {
        status: 500,
        data: { success: false, message: error.message || 'Failed to update court' }
      };
    }
  },
  
  delete_court: async (id) => {
    try {
      return await request.delete(`/courts/${id}`, true);
    } catch (error) {
      console.error('[Admin API] Error in delete_court:', error);
      return {
        status: 500,
        data: { success: false, message: error.message || 'Failed to delete court' }
      };
    }
  },
  
  get_transactions_by_date_range: async (startDate, endDate, page = 1, limit = 10) => {
    const queryParams = new URLSearchParams({
      startDate, 
      endDate,
      page,
      limit
    }).toString();
    
    const response = await request.get(`/bookings/admin/all?${queryParams}`, true);
    logResponseData('get_transactions_by_date_range', response);
    return response;
  },
  
  get_transactions_by_month: async (year, month, page = 1, limit = 100) => {
    const startDate = `${year}-${month.padStart(2, '0')}-01`;
    let endMonth = parseInt(month, 10);
    let endYear = parseInt(year, 10);
    
    if (endMonth === 12) {
      endMonth = 1;
      endYear += 1;
    } else {
      endMonth += 1;
    }
    
    const endDate = `${endYear}-${endMonth.toString().padStart(2, '0')}-01`;
    
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
      page,
      limit
    }).toString();
    
    const response = await request.get(`/bookings/admin/all?${queryParams}`, true);
    logResponseData('get_transactions_by_month', response);
    return response;
  },
  
  get_pending_payments: async (page = 1, limit = 10) => {
    try {
      const response = await request.get(`payments/admin/pending?page=${page}&limit=${limit}`, true);
      return response;
    } catch (error) {
      console.error('[Approver API] Error in get_pending_courts:', error);
      throw error;
    }
  },
  
  verify_payment: async (data) => {
    try {
      const response = await request.post('/payments/admin/verify', data, true);
      return response;
    } catch (error) {
      console.error('Error in verify_payment:', error);
      throw error;
    }
  },
  
  get_verification_history: async (page = 1, limit = 10) => {
    const response = await request.get(`/payments/verification/history?page=${page}&limit=${limit}`, true);
    logResponseData('get_verification_history', response);
    return response;
  },
  
  get_all_users: async (page = 1, limit = 10) => {
    const response = await request.get(`/users/admin/users?page=${page}&limit=${limit}`, true);
    logResponseData('get_all_users', response);
    return response;
  },
  
  update_user_role: async (data) => {
    const response = await request.put('/users/admin/role', data, true);
    logResponseData('update_user_role', response);
    return response;
  },

  get_user_profile: async () => {
    try {
      const response = await request.get('/users/profile', true);
      return response;
    } catch (error) {
      console.error('[Admin API] Error getting user profile:', error);
      throw error;
    }
  },

  get_owner_courts: async () => {
    try {
      let userData;
      
      // Try to get user data from SecureLS first
      try {
        userData = ls.get('userData');
      } catch (e) {
        // Fallback to localStorage if SecureLS fails
        const storedData = localStorage.getItem('userData');
        if (storedData) {
          userData = JSON.parse(storedData);
        }
      }
      
      if (!userData) {
        throw new Error('User data not found');
      }
      
      const user = userData;
      if (!user || !user.id) {
        throw new Error('Invalid user data');
      }
      
      const ownerId = user.id;
      const response = await request.get(`/courts/owner/${ownerId}`, true);
      return response;
    } catch (error) {
      console.error('[Admin API] Error in get_owner_courts:', error);
      return {
        status: 500,
        data: { success: false, message: error.message || 'Failed to fetch owner courts' }
      };
    }
  },

  get_admin_courts: async (page = 1, limit = 10) => {
    try {
      const response = await request.get(`/court-admin/my-courts`, true);
      logResponseData('get_admin_courts', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error getting admin courts:', error);
      return {
        status: error.response?.status || 500,
        data: { success: false, message: error.message || 'Failed to fetch admin courts' }
      };
    }
  },

  get_courts_for_user: async (page = 1, limit = 100) => {
    try {
      let userData;
      
      // Try to get user data from SecureLS first
      try {
        userData = ls.get('userData');
      } catch (e) {
        // Fallback to localStorage if SecureLS fails
        const storedData = localStorage.getItem('userData');
        if (storedData) {
          userData = JSON.parse(storedData);
        }
      }
      
      if (!userData) {
        throw new Error('User data not found');
      }
      
      const user = userData;
      if (!user || !user.id) {
        throw new Error('Invalid user data');
      }
      
      const userId = user.id;
      const userRole = user.role;
      const response = await request.get(`/courts?page=${page}&limit=${limit}`, true);
      const responseData = response.data;
      
      // Filter courts if user is an owner
      if (userRole === 'owner' && responseData.data && Array.isArray(responseData.data)) {
        const ownerCourts = responseData.data.filter(court => 
          (court.created_by === userId || court.createdBy === userId)
        );
        return {
          status: 200,
          data: {
            success: true,
            data: ownerCourts,
            pagination: responseData.pagination || {
              currentPage: 1,
              totalPages: 1,
              totalCourts: ownerCourts.length
            }
          }
        };
      }
      
      return response;
    } catch (error) {
      console.error('[Admin API] Error in get_courts_for_user:', error);
      return {
        status: error.status || 500,
        data: { 
          success: false, 
          message: error.message || 'Failed to fetch courts' 
        }
      };
    }
  },
  
  get_payment_methods: async () => {
    try {
      const response = await request.get('/payments/methods', false);
      logResponseData('get_payment_methods', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error getting payment methods:', error);
      return {
        status: 500,
        data: { success: false, message: error.message || 'Failed to fetch payment methods' }
      };
    }
  },

  get_cancellation_requests: async (page = 1, limit = 10, status = 'pending') => {
    try {
      const response = await request.get(`/admin/cancellation-requests?page=${page}&limit=${limit}&status=${status}`, true);
      logResponseData('get_cancellation_requests', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error getting cancellation requests:', error);
      // Return a proper error response instead of throwing
      return {
        status: error.response?.status || 500,
        data: { 
          success: false, 
          message: error.message || 'Failed to fetch cancellation requests',
          data: [],
          pagination: { currentPage: page, totalPages: 0, totalRecords: 0 }
        }
      };
    }
  },

  process_cancellation_request: async (requestData) => {
    try {
      const response = await request.post('/admin/cancellation-requests/process', requestData, true);
      logResponseData('process_cancellation_request', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error processing cancellation request:', error);
      return {
        status: error.response?.status || 500,
        data: { success: false, message: error.message || 'Failed to process cancellation request' }
      };
    }
  },

  get_cancellation_request_details: async (requestId) => {
    try {
      const response = await request.get(`/admin/cancellation-requests/${requestId}`, true);
      logResponseData('get_cancellation_request_details', response);
      return response;
    } catch (error) {
      console.error('Error getting cancellation request details:', error);
      throw error;
    }
  },

  get_cancellation_history: async (page = 1, limit = 10, status = 'all') => {
    try {
      const response = await request.get(`/admin/cancellation-requests/history?page=${page}&limit=${limit}&status=${status}`, true);
      logResponseData('get_cancellation_history', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error getting cancellation history:', error);
      return {
        status: error.response?.status || 500,
        data: { 
          success: false, 
          message: error.message || 'Failed to fetch cancellation history',
          data: [],
          pagination: { currentPage: page, totalPages: 0, totalRecords: 0 }
        }
      };
    }
  },

  get_cancellation_statistics: async () => {
    try {
      const response = await request.get('/admin/cancellation-requests/statistics', true);
      logResponseData('get_cancellation_statistics', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error getting cancellation statistics:', error);
      return {
        status: error.response?.status || 500,
        data: { success: false, message: error.message || 'Failed to fetch statistics' }
      };
    }
  },

  get_refund_history: async (page = 1, limit = 10, refundStatus = 'all') => {
    try {
      const response = await request.get(`/admin/refunds?page=${page}&limit=${limit}&refundStatus=${refundStatus}`, true);
      logResponseData('get_refund_history', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error getting refund history:', error);
      return {
        status: error.response?.status || 500,
        data: { 
          success: false, 
          message: error.message || 'Failed to fetch refund history',
          data: { refunds: [], pagination: { currentPage: page, totalPages: 0, totalRecords: 0 } }
        }
      };
    }
  },



  get_pending_refunds: async (page = 1, limit = 10) => {
    try {
      // Use the refund history endpoint with pending status
      const response = await request.get(`/admin/refunds?page=${page}&limit=${limit}&refundStatus=pending`, true);
      logResponseData('get_pending_refunds', response);
      
      // Transform the response to match expected format
      if (response && response.data && response.data.success) {
        const refunds = response.data.data?.refunds || [];
        const pagination = response.data.data?.pagination || { currentPage: page, totalPages: 0, totalRecords: 0 };
        
        return {
          status: 200,
          data: {
            success: true,
            data: refunds,
            pagination: pagination
          }
        };
      }
      
      return response;
    } catch (error) {
      console.error('[Admin API] Error getting pending refunds:', error);
      return {
        status: error.response?.status || 500,
        data: { 
          success: false, 
          message: error.message || 'Failed to fetch pending refunds',
          data: [],
          pagination: { currentPage: page, totalPages: 0, totalRecords: 0 }
        }
      };
    }
  },

  process_refund: async (refundData) => {
    try {
      // Use the new refund status update endpoint
      const response = await request.put(`/admin/refunds/${refundData.cancellationId}/status`, {
        refundStatus: 'completed',
        adminNotes: refundData.notes || 'Refund processed by admin'
      }, true);
      logResponseData('process_refund', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error processing refund:', error);
      return {
        status: error.response?.status || 500,
        data: { success: false, message: error.message || 'Failed to process refund' }
      };
    }
  },

  update_refund_status: async (refundId, statusData) => {
    try {
      // Validate refundId before making the request
      if (!refundId || refundId === 'undefined' || refundId === undefined) {
        throw new Error('Invalid refund ID provided');
      }
      
      const response = await request.put(`/admin/refunds/${refundId}/status`, statusData, true);
      logResponseData('update_refund_status', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error updating refund status:', error);
      return {
        status: error.response?.status || 500,
        data: { success: false, message: error.message || 'Failed to update refund status' }
      };
    }
  },

  get_refund_summary: async () => {
    try {
      const response = await request.get('/admin/refunds/summary', true);
      logResponseData('get_refund_summary', response);
      return response;
    } catch (error) {
      console.error('[Admin API] Error getting refund summary:', error);
      return {
        status: error.response?.status || 500,
        data: { success: false, message: error.message || 'Failed to fetch refund summary' }
      };
    }
  },
};

// User API methods for cancellation
const user_api = {
  request_booking_cancellation: async (cancellationData) => {
    try {
      const response = await request.post('/users/booking/cancel-request', cancellationData, true);
      logResponseData('request_booking_cancellation', response);
      return response;
    } catch (error) {
      console.error('[User API] Error requesting booking cancellation:', error);
      return {
        status: error.response?.status || 500,
        data: { success: false, message: error.message || 'Failed to request cancellation' }
      };
    }
  },

  get_user_cancellation_requests: async (page = 1, limit = 10) => {
    try {
      const response = await request.get(`/users/cancellation-requests?page=${page}&limit=${limit}`, true);
      logResponseData('get_user_cancellation_requests', response);
      return response;
    } catch (error) {
      console.error('[User API] Error getting user cancellation requests:', error);
      return {
        status: error.response?.status || 500,
        data: { 
          success: false, 
          message: error.message || 'Failed to fetch cancellation requests',
          data: { requests: [], pagination: { currentPage: page, totalPages: 0, totalRecords: 0 } }
        }
      };
    }
  },

   /**
   * Get enhanced user bookings with payment status tracking
   * @returns {Promise} API response
   */
  get_user_bookings: async () => {
    try {
      // Try enhanced endpoint first
      const response = await request.get('/users/bookings/enhanced', true);
      
      if (response && response.data) {
        // Transform data to include enhanced payment status
        const bookings = Array.isArray(response.data.data) ? response.data.data : 
                        Array.isArray(response.data) ? response.data : [];
        
        const enhancedBookings = bookings.map(booking => {
          // Determine enhanced payment status based on booking and cancellation status
          let enhancedPaymentStatus = booking.payment_status || booking.paymentStatus || 'pending';
          
          // Check cancellation and refund status
          if (booking.status === 'cancelled' || booking.bookingStatus === 'cancelled') {
            if (booking.refund_status === 'completed') {
              enhancedPaymentStatus = 'fully_refunded';
            } else if (booking.refund_status === 'pending') {
              enhancedPaymentStatus = 'refund_pending';
            } else {
              enhancedPaymentStatus = 'cancelled';
            }
          } else if (booking.status === 'cancellation_requested' || booking.bookingStatus === 'cancellation_requested') {
            enhancedPaymentStatus = 'refund_pending';
          }
          
          return {
            ...booking,
            enhanced_payment_status: enhancedPaymentStatus,
            original_payment_status: booking.payment_status || booking.paymentStatus
          };
        });
        
        return {
          ...response,
          data: {
            ...response.data,
            data: enhancedBookings
          }
        };
      }
      
      return response;
    } catch (error) {
      console.error('[User API] Error getting enhanced user bookings, falling back to regular:', error);
      // Fallback to regular endpoint
      return await request.get('/bookings/user', true);
    }
  }
};


export default admin;
export { user_api };